import { useContext } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { SATOSPortal } from "@/DataAdapter/SATOSPortal";

import { CreateCompanyRequest } from "@/model/Company/CreateCompanyRequest";
import { ChangeUserOrganizationRequest } from "@/model/User/User";
import { UserContext } from "@/model/User/UserContext";

import { ApplicationQueryKeys } from "@/service/ApplicationQueries";
import { ModulesQueryKeys } from "@/service/Settings/ModulesQueries";

export const useMutateUserOrganization = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: ChangeUserOrganizationRequest) =>
			SATOSPortal.ChangeUserOrganization(request),
			onSettled: () => {
				queryClient.invalidateQueries({queryKey: [ApplicationQueryKeys.user]});
				queryClient.invalidateQueries({queryKey: [ModulesQueryKeys.organizationModules]});
			},
		},
	);

	return mutation.mutateAsync;
};

export const useMutateUpdateUser = () => {
	const user = useContext(UserContext);
	const queryClient = useQueryClient();
	const { mutateAsync: UpdateUser } = useMutation({
		mutationFn: ({ request, id }: { request: any; id: number }) => {
			return SATOSPortal.UpdateUser(request, id);
		},
		onSuccess: (data: any) => {
			queryClient.invalidateQueries({queryKey: [
				ApplicationQueryKeys.user,
				user?.CompanyId,
			]});
		},
		onSettled: (data: any) => {
			queryClient.invalidateQueries({queryKey: [ApplicationQueryKeys.user]});
		},
	});
	return UpdateUser;
};


export const useMutateCopyUserSettings = () => {
	const queryClient = useQueryClient();
	const { mutateAsync: CopyUserSettings } = useMutation({
		mutationFn: (request: { targetUser: number; sourceUser: number }) => {
			return SATOSPortal.CopyUserSettings(request);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({queryKey: [ApplicationQueryKeys.user]});
		},
		onSettled: () => {
			queryClient.invalidateQueries({queryKey: [ApplicationQueryKeys.user]});
		},
	});
	return CopyUserSettings;
}


export const useMutateCreateOrganization = () => {
	const user = useContext(UserContext);
	const queryClient = useQueryClient();
	const { mutateAsync: CreateOrganization } = useMutation({
		mutationFn: (request: CreateCompanyRequest) => {
			return SATOSPortal.CreateOrganization(request);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({queryKey: [ApplicationQueryKeys.companies]});
		},
		onSettled: () => {
			queryClient.invalidateQueries({queryKey: [
				ApplicationQueryKeys.companies,
				user?.CompanyId,
			]});
		},
	});

	return CreateOrganization;
};
